import type { ReactNode } from 'react'

import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

export type StatStripCell = {
  /** Stable identity for the cell (used as the React key). */
  id: string
  label: ReactNode
  value: ReactNode
}

type StatStripProps = {
  cells: StatStripCell[]
  className?: string
  'data-tst'?: string
}

/**
 * A flat, hairline-divided row of secondary figures.
 *
 * Deliberately the quiet counterpart to `DashboardKpiTile`: no gradient, no
 * accent chip, smaller type. A page that gives every number the tile treatment
 * has no hierarchy left to spend, so supporting figures live here and the
 * accent stays on the handful of metrics the page actually exists to answer.
 *
 * Renders nothing for an empty `cells` array, so callers can pass a filtered
 * list without guarding.
 */
export const StatStrip = ({ cells, className, 'data-tst': dataTst }: StatStripProps) => {
  if (cells.length === 0) {
    return null
  }

  return (
    <div
      data-tst={dataTst}
      className={cn(
        // gap-[1px] rather than gap-px: the preset REPLACES Tailwind's spacing
        // scale and has no `px` step. The 1px gap over a divider-coloured
        // background is what draws the hairlines between cells.
        //
        // flex-wrap rather than a `grid-cols-[repeat(auto-fit,…)]` grid: an
        // auto-fit grid leaves a visibly empty track when the cell count does
        // not divide by the column count (three cells over two columns), while
        // a flex row lets the last cell stretch into the space instead.
        'flex flex-wrap gap-[1px] overflow-hidden rounded-sm bg-divider',
        className,
      )}
    >
      {cells.map((cell) => (
        <div
          key={cell.id}
          // Wraps rather than squeezes: four 4-char figures in a 320px row is
          // unreadable, and the strip is supporting detail, not the headline.
          className="flex min-w-[140px] flex-1 flex-col gap-0.5 bg-paper-elevation2 px-4 py-3"
        >
          <Typography
            className="font-heading font-semibold tabular-nums leading-120"
            variant="h6"
          >
            {cell.value}
          </Typography>
          <Typography
            // tracking-[0.06em] matches the dashboard-kpi-tile eyebrow. No
            // letterSpacing token covers em-based values (they are all px).
            className="text-xs uppercase tracking-[0.06em]"
            color="text.secondary"
            variant="caption"
          >
            {cell.label}
          </Typography>
        </div>
      ))}
    </div>
  )
}
